import React from "react";
import { Scan, Layers, Cpu, Activity, Database, Crosshair, Zap, Globe, Box } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  title: "Ground Handling & Cargo",
  description: "Full-spectrum ramp services and air cargo logistics management, ensuring secure and rapid freight processing.",
  icon: Scan,
  specs: ["Service: Full Ramp Support", "Cargo: Export & Import", "Standard: IATA Compliant", "Safety: 100% Audit Track"]
}, {
  title: "Permits & Logistics Support",
  description: "Rapid landing permit acquisition, fuel supply coordination, and premium catering and hotel arrangements for crews.",
  icon: Layers,
  specs: ["Permits: Overflight & Landing", "Fuel: Jet A-1 Availability", "Support: Hotel & Catering", "Response: 24/7 Operations"]
}, {
  title: "Specialized Aviation Services",
  description: "VIP protocol services, aircraft chartering for cargo or passengers, and dedicated luxury crew transportation.",
  icon: Cpu,
  specs: ["Charter: Cargo & Passenger", "Protocol: VIP Fast-track", "Transport: Crew Shuttle", "Efficiency: Rapid Turnaround"]
}];
export default function PrecisionIntelligence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="35526" id="elite-ground-handling-portfolio" ref={ref} className={`
        dark bg-background text-foreground py-24 relative overflow-hidden
        transition-opacity duration-1000
        ${isVisible ? "opacity-100" : "opacity-0"}
      `}>
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute inset-0" style={{
        backgroundImage: `
              linear-gradient(to right, var(--border) 1px, transparent 1px),
              linear-gradient(to bottom, var(--border) 1px, transparent 1px)
            `,
        backgroundSize: '40px 40px'
      }} />
        <div className="absolute inset-0 bg-radial-[circle_at_center,transparent_0%,var(--background)_80%]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-16 max-w-3xl">
          <div className="flex items-center gap-2 text-primary font-mono text-sm mb-4 tracking-wider uppercase">
            <Activity className="w-4 h-4 animate-pulse" />
            Operational Status: Safety • Speed • Efficiency
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Elite <span className="text-primary">Handling Portfolio</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl">We are an aircraft ground handling company specialized in air cargo and logistics services, supporting airlines, crews, and passengers with uncompromising professionalism in all airports of the Democratic Republic of Congo</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, i) => <div key={i} data-index={i} className="group perspective-1000">
              <Card className="
                h-full bg-card/40 backdrop-blur-sm border-border/50 
                transition-all duration-500 ease-out
                group-hover:border-primary group-hover:shadow-[0_0_30px_-10px_var(--primary)]
                group-hover:-translate-y-1
                relative overflow-hidden
              ">
                {}
                <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-primary/0 group-hover:border-primary transition-colors duration-300" />
                <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-primary/0 group-hover:border-primary transition-colors duration-300" />
                <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-primary/0 group-hover:border-primary transition-colors duration-300" />
                <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-primary/0 group-hover:border-primary transition-colors duration-300" />

                <CardHeader className="pb-2">
                  <div className="mb-4 relative w-12 h-12 flex items-center justify-center">
                    <div className="absolute inset-0 bg-primary/10 rounded-full scale-0 group-hover:scale-150 transition-transform duration-500" />
                    <feature.icon className="w-10 h-10 text-primary relative z-10 transition-all duration-700 group-hover:rotate-[360deg]" strokeWidth={1.5} />
                  </div>
                  <CardTitle className="text-2xl font-bold group-hover:text-primary transition-colors">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <CardDescription className="text-base leading-relaxed text-muted-foreground">
                    {feature.description}
                  </CardDescription>

                  {}
                  <div className="
                    border-t border-border/50 pt-4 mt-4
                    opacity-60 max-h-0 overflow-hidden
                    group-hover:opacity-100 group-hover:max-h-[200px]
                    transition-all duration-500 ease-in-out
                  ">
                    <h4 className="text-xs font-mono text-primary uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Database className="w-3 h-3" />
                      Technical Specs
                    </h4>
                    <ul className="space-y-2">
                      {feature.specs.map((spec, j) => <li key={j} className="flex items-center gap-2 text-sm font-mono text-muted-foreground">
                          <span className="w-1 h-1 bg-primary rounded-full" />
                          <span dangerouslySetInnerHTML={{
                      __html: spec.replace(/:/, ':<span class="text-foreground ml-1">').concat('</span>')
                    }} />
                        </li>)}
                    </ul>
                  </div>
                </CardContent>

                {}
                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-20 transition-opacity duration-300">
                  <div className="w-full h-[1px] bg-primary shadow-[0_0_10px_2px_var(--primary)] animate-[scan_3s_linear_infinite]" />
                </div>
              </Card>
            </div>)}
        </div>
      </div>

      <style jsx global>{`
        @keyframes scan {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(500px); }
        }
      `}</style>
    </section>;
}